import { useState, useEffect } from 'react';
import { Copy, Check } from '@phosphor-icons/react';
import { chatApi } from '../../../shared/api/chatApi';
import { Modal } from '../../../shared/components/layout/Modal';

interface ShareChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  isPublic: boolean;
}

export function ShareChatDialog({ isOpen, onClose, chatId, isPublic: initialIsPublic }: ShareChatDialogProps) {
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [publicId, setPublicId] = useState<string | null>(null);

  const shareUrl = `${window.location.origin}/shared/${publicId}`;

  const handleTogglePublic = async () => {
    setIsLoading(true);
    try {
      const newPublicId = !isPublic ? crypto.randomUUID() : null;
      await chatApi.updateChatSharing(chatId, !isPublic, newPublicId);
      setPublicId(newPublicId);
      setIsPublic(!isPublic);
    } catch (error) {
      console.error('Error updating chat sharing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function loadPublicId() {
      if (isPublic) {
        const chat = await chatApi.getChat(chatId);
        setPublicId(chat.public_id);
      }
    }
    loadPublicId();
  }, [chatId, isPublic]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Chat"
      variant="dialog"
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Make chat public</h3>
            <p className="text-sm text-gray-500">
              Allow anyone with the link to view this chat
            </p>
          </div>
          <button
            type="button"
            onClick={handleTogglePublic}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isPublic ? 'bg-blue-600' : 'bg-gray-200'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isPublic ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {isPublic && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isCopied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
} 